// ENG-6033: kanban-task rating aggregation for the platform-admin Dashboard.
//
// Pure, DB-agnostic, browser-safe aggregation that turns rated kanban rows into
// an "are agents improving" signal. Callers (the cross-org admin Dashboard via
// the Next.js webapp) fetch rated rows over a window and hand them here; keeping
// the bucketing + moving-average maths pure makes it unit-testable without a
// database and shareable across packages. Mirrors conversations/metrics.ts.
//
// SCORE MODEL (council-reviewed — see ENG-6033 plan review):
//   - Ratings are a thumbs scale: -1 (down) / 0 (neutral) / +1 (up). Score is a
//     TRAILING 28-DAY (4-week) MOVING AVERAGE of the ±1 ratings only — neutral
//     (0) ratings are EXCLUDED from the mean (they carry no direction and would
//     drag every score toward zero) but are still counted as context.
//   - The MA window is FIXED at 28d regardless of the display period. The period
//     (24h/7d/30d) only controls how far back the chart is drawn and how finely
//     it's sampled. A "4-week MA over a 24h window" is only coherent because the
//     CALLER fetches `period + 28d` of rows; each sample point then averages the
//     28 days of ratings ending at that point. See loadRatings in the route.
//   - Ratings are SPARSE and self-selected, so the moving average smooths what a
//     per-bucket mean would render as noise. A sample point with zero ±1 ratings
//     in its trailing window scores `null` (a genuine gap — the UI must NOT
//     interpolate across it) rather than a misleading 0.
//   - `n` (the count behind each score) travels with every score so a 2-rating
//     average is never silently displayed as authoritative as a 200-rating one.

import type { ConversationMetricsPeriod } from '../conversations/metrics.js';

/** Trailing window for the moving average: 4 weeks, fixed (independent of the display period). */
export const RATING_MA_WINDOW_MS = 28 * 24 * 60 * 60 * 1000;
export const RATING_MA_WINDOW_DAYS = 28;

/** A `trend` is only emitted when its delta clears this band, so noise reads as "flat". */
const TREND_EPSILON = 0.05;

/** Display window length + sampling granularity per period (mirrors conversation metrics). */
const PERIOD_CONFIG: Record<
  ConversationMetricsPeriod,
  { windowMs: number; bucket: 'hour' | 'day' }
> = {
  '24h': { windowMs: 24 * 60 * 60 * 1000, bucket: 'hour' },
  '7d': { windowMs: 7 * 24 * 60 * 60 * 1000, bucket: 'day' },
  '30d': { windowMs: 30 * 24 * 60 * 60 * 1000, bucket: 'day' },
};

/**
 * One rated kanban row. The caller filters to `rating IS NOT NULL` and fetches
 * back to `periodStart - 28d` so the trailing MA is correct from the first
 * displayed bucket. `code_name`/`display_name` ride along so the helper stays
 * free of any agent lookup.
 */
export interface KanbanRatingRow {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  /** Owning organisation's display name — shown after the agent name on the cross-org admin surface. */
  org_name: string | null;
  /** Thumbs scale: -1 | 0 | +1. Anything else is ignored defensively. */
  rating: number;
  rated_at: string;
}

/** One sample of the trailing-28d moving average. */
export interface RatingTrendPoint {
  /** ISO sample timestamp (UTC bucket start). */
  bucket: string;
  /** Trailing-28d mean of ±1 ratings ending at `bucket`; null when none — a real gap, do NOT interpolate. */
  score: number | null;
  /** ±1 ratings inside the trailing window — the sample size behind `score`. */
  n: number;
}

export type RatingTrend = 'up' | 'flat' | 'down';

/** Per-agent standing as of `now`, for the ranked table beneath the org line. */
export interface AgentRatingSummary {
  agentId: string;
  codeName: string;
  displayName: string | null;
  /** Owning organisation's display name (cross-org disambiguation); null if unknown. */
  orgName: string | null;
  /** Trailing-28d score as of `now`; null if no ±1 ratings in the last 28d. */
  score: number | null;
  /** ±1 ratings in the trailing-28d window as of `now` (the n behind `score`). */
  ratedCount: number;
  /** Neutral (0) ratings in the same window — context, not part of `score`. */
  neutralCount: number;
  /** score(now) vs score(periodStart): up/flat/down; null if either endpoint is undefined. */
  trend: RatingTrend | null;
}

export interface KanbanRatingsResult {
  period: ConversationMetricsPeriod;
  bucket: 'hour' | 'day';
  period_start: string;
  ma_window_days: number;
  /** Org-wide trailing-28d MA sampled across the display window (continuous axis; gaps are null). */
  series: RatingTrendPoint[];
  /** Per-agent standing as of `now`, ranked best-first. */
  perAgent: AgentRatingSummary[];
  /** ±1 ratings across all agents inside the trailing-28d window as of `now`. */
  overallRatedCount: number;
}

/** Truncate a date to the start of its UTC hour or day. */
function truncateUtc(d: Date, bucket: 'hour' | 'day'): Date {
  const t = new Date(d);
  t.setUTCMinutes(0, 0, 0);
  if (bucket === 'day') t.setUTCHours(0);
  return t;
}

interface NormalisedRating {
  t: number;
  /** +1 / -1 for directional ratings, 0 for neutral. */
  r: number;
}

interface TrailingStats {
  score: number | null;
  n: number;
  neutral: number;
}

/**
 * Trailing-28d statistics over already-sorted ratings across the inclusive
 * window `[endMs - 28d, endMs]`. Score is the mean of ±1 ratings only; neutral
 * 0s are counted separately. `null` score when the window holds no ±1 rating.
 */
function trailingStats(sorted: NormalisedRating[], endMs: number): TrailingStats {
  const startMs = endMs - RATING_MA_WINDOW_MS;
  let sum = 0;
  let n = 0;
  let neutral = 0;
  for (const { t, r } of sorted) {
    if (t < startMs) continue;
    if (t > endMs) break; // sorted ascending — nothing later is in-window
    if (r === 0) {
      neutral += 1;
    } else if (r === 1 || r === -1) {
      sum += r;
      n += 1;
    }
  }
  return { score: n > 0 ? sum / n : null, n, neutral };
}

function classifyTrend(start: number | null, end: number | null): RatingTrend | null {
  if (start === null || end === null) return null;
  const delta = end - start;
  if (delta > TREND_EPSILON) return 'up';
  if (delta < -TREND_EPSILON) return 'down';
  return 'flat';
}

/**
 * Aggregate rated kanban rows into an org-wide trailing-28d moving-average
 * series plus a per-agent standing table. `now` is injectable for deterministic
 * tests. Rows are expected to span `[periodStart - 28d, now]`; rows outside the
 * trailing windows simply never contribute.
 */
export function aggregateKanbanRatings(
  rows: KanbanRatingRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
): KanbanRatingsResult {
  const { windowMs, bucket } = PERIOD_CONFIG[period];
  const nowMs = now.getTime();
  const periodStart = new Date(nowMs - windowMs);
  const periodStartMs = periodStart.getTime();

  // Normalise + sort once; per-agent buckets reuse the same ascending order so
  // trailingStats can early-break.
  const all: NormalisedRating[] = [];
  const byAgent = new Map<
    string,
    { codeName: string; displayName: string | null; orgName: string | null; ratings: NormalisedRating[] }
  >();
  for (const row of rows) {
    const ts = new Date(row.rated_at).getTime();
    if (!Number.isFinite(ts) || ts > nowMs) continue; // ignore unparseable / future timestamps
    const r = row.rating === 1 ? 1 : row.rating === -1 ? -1 : row.rating === 0 ? 0 : NaN;
    if (Number.isNaN(r)) continue; // defensive: DB constrains to -1/0/1, but never trust the wire
    const norm: NormalisedRating = { t: ts, r };
    all.push(norm);

    let entry = byAgent.get(row.agent_id);
    if (!entry) {
      entry = {
        codeName: row.code_name,
        displayName: row.display_name,
        orgName: row.org_name,
        ratings: [],
      };
      byAgent.set(row.agent_id, entry);
    }
    entry.ratings.push(norm);
  }
  all.sort((a, b) => a.t - b.t);
  for (const entry of byAgent.values()) entry.ratings.sort((a, b) => a.t - b.t);

  // Org-wide trailing-28d MA sampled across a continuous display axis.
  const stepMs = bucket === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const firstBucket = truncateUtc(periodStart, bucket).getTime();
  const series: RatingTrendPoint[] = [];
  for (let t = firstBucket; t <= nowMs; t += stepMs) {
    const { score, n } = trailingStats(all, t);
    series.push({ bucket: new Date(t).toISOString(), score, n });
  }

  // Per-agent standing as of `now`, with a trend vs the start of the window.
  const perAgent: AgentRatingSummary[] = [];
  for (const [agentId, entry] of byAgent.entries()) {
    const atNow = trailingStats(entry.ratings, nowMs);
    if (atNow.n === 0 && atNow.neutral === 0) continue; // no rating in the trailing window → omit
    const atStart = trailingStats(entry.ratings, periodStartMs);
    perAgent.push({
      agentId,
      codeName: entry.codeName,
      displayName: entry.displayName,
      orgName: entry.orgName,
      score: atNow.score,
      ratedCount: atNow.n,
      neutralCount: atNow.neutral,
      trend: classifyTrend(atStart.score, atNow.score),
    });
  }
  // Best-first: highest score, then most-rated (more confident), then name.
  perAgent.sort((a, b) => {
    const sa = a.score ?? Number.NEGATIVE_INFINITY;
    const sb = b.score ?? Number.NEGATIVE_INFINITY;
    if (sb !== sa) return sb - sa;
    if (b.ratedCount !== a.ratedCount) return b.ratedCount - a.ratedCount;
    return a.codeName.localeCompare(b.codeName);
  });

  return {
    period,
    bucket,
    period_start: periodStart.toISOString(),
    ma_window_days: RATING_MA_WINDOW_DAYS,
    series,
    perAgent,
    overallRatedCount: trailingStats(all, nowMs).n,
  };
}
